import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as uuid from 'uuid';

import { getBranches, getStores, getUploadUrl, registerWarranty, searchSerialNumber } from '@/controller/warranty';
import { toast } from '@/shared/components/UI/Toast';
import useLoading from '@/shared/hooks/useLoading';
import useMessageHook from '@/shared/hooks/useMessageHook';
import { useTranslations } from '@/translations/useTranslations.js';

import SerialNumberExampleModal from './components/SerialNumberExampleModal';

const RegisterProduct = () => {
    const { t } = useTranslations();
    const nav = useNavigate();
    const { requireUpload } = useMessageHook();

    // Form state
    const [formData, setFormData] = useState({
        purchase_form_store: '',
        purchase_form_branch: '',
        branch_name: '',
        date_of_purchase: '',
        serial_number: '',
        upload_file: null,
    });

    // Form validation errors
    const [errors, setErrors] = useState({});

    // Component state
    const [isOther, setOther] = useState(false);
    const [store, setStore] = useState([]);
    const [branch, setBranch] = useState([]);
    const [serialNumber, setSerialNumber] = useState('');
    const [product, setProduct] = useState({});

    // UI state
    const [egModal, setEgModal] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const { loading, tip, toggle } = useLoading();

    // Load stores on component mount
    useEffect(() => {
        const getStoreList = async () => {
            const ret = await getStores();
            if (ret.status === 200) {
                const sl = Array.isArray(ret.data?.data) ? ret.data?.data : [];
                let storelist = [{ value: '', title: '-' }];
                sl.forEach((e, i) => {
                    storelist.push({
                        value: e.id,
                        title: e.name,
                        branches: e.branches,
                    });
                });
                setStore(storelist);
            }
        };

        getStoreList();
    }, []);

    // Load branches on component mount
    useEffect(() => {
        const getBranchList = async () => {
            try {
                const ret = await getBranches();
                if (ret.status === 200) {
                    let branchList = [];
                    const bl = Array.isArray(ret.data?.data) ? ret.data?.data : [];
                    bl.forEach((e, i) => {
                        branchList.push({
                            title: e.name,
                            value: e.id,
                            store_id: e.store_id,
                        });
                    });
                    setBranch(branchList);
                }
            } catch (e) {}
        };
        getBranchList();
    }, []);

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    // Handle store selection
    const onChgStore = (value) => {
        handleFieldChange('purchase_form_store', value);
        setFormData((prev) => ({
            ...prev,
            purchase_form_branch: '',
            branch_name: '',
        }));

        if (value === '') {
            setOther(false);
            setBranch([]);
        } else {
            setOther(false);
            let branches = [];
            let foundStore = null;

            store.forEach((e2, i2) => {
                if (e2.value == value) {
                    // Use == instead of === to handle string/number comparison
                    foundStore = e2;
                    branches = e2.branches;
                }
            });

            if (!foundStore) {
                console.log('No matching store found!');
                return;
            }

            if (!branches || !Array.isArray(branches)) {
                console.log('No branches found or branches is not an array:', branches);
                setBranch([]);
                return;
            }

            let branchList = [];
            branches.forEach((e3, i3) => {
                console.log('Processing branch:', e3);
                branchList.push({
                    value: e3.uuid,
                    title: e3.name,
                });
            });
            setBranch(branchList);
            console.log('Updated branches:', branchList);
        }
    };

    // Handle branch selection
    const onChgBranch = (value) => {
        handleFieldChange('purchase_form_branch', value);
        const selectedBranch = branch.find((x) => x.value === value);
        if (selectedBranch && selectedBranch.title.toLowerCase() === 'others') {
            setOther(true);
        } else {
            setOther(false);
        }
    };

    // Validate serial number during form submission
    const validateSerialNumber = async (serialNumber) => {
        const ret = await searchSerialNumber(serialNumber);

        if (ret.data.success === true) {
            if (ret.data.data.length > 0) {
                return {
                    isValid: true,
                    product: ret.data.data[0],
                };
            } else {
                return {
                    isValid: false,
                    error: 'Product with this serial number not found in our inventory',
                };
            }
        } else if (ret.status !== 401) {
            // Don't show toast for 401 errors - handled by global interceptor
            return {
                isValid: false,
                error: ret.data.error.message || 'Failed to validate serial number',
            };
        } else {
            return {
                isValid: false,
                error: 'Authentication required',
            };
        }
    };

    // Handle file upload
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                return;
            }
            handleFieldChange('upload_file', file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                return;
            }
            handleFieldChange('upload_file', file);
        }
    };

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.purchase_form_store) {
            newErrors.purchase_form_store = t('form.please_select');
        }

        if (!formData.purchase_form_branch) {
            newErrors.purchase_form_branch = t('form.please_select');
        }

        if (isOther && !formData.branch_name.trim()) {
            newErrors.branch_name = 'Branch name is required';
        }

        if (!formData.date_of_purchase) {
            newErrors.date_of_purchase = 'Date of purchase is required';
        }

        if (!formData.serial_number.trim()) {
            newErrors.serial_number = 'Serial number is required';
        }

        if (!formData.upload_file) {
            newErrors.upload_file = requireUpload('proof_of_purchase');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Set field error
    const setError = (name, error) => {
        setErrors((prev) => ({
            ...prev,
            [name]: error,
        }));
    };

    // Form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Validate serial number before proceeding with registration
        toggle('Validating serial number...');

        const serialValidation = await validateSerialNumber(formData.serial_number);

        if (!serialValidation.isValid) {
            toggle(); // Stop loading
            setError('serial_number', serialValidation.error);
            toast.error(serialValidation.error);
            return;
        }

        // Set the validated product for display
        setProduct(serialValidation.product);

        // Proceed with registration
        await proceedRegister(formData);
    };

    // Register warranty
    const proceedRegister = async (v) => {
        toggle('Loading...');

        const date = v.date_of_purchase;
        const purchaseDate = dayjs(date).format('YYYY-MM-DD');
        try {
            const data = {};

            data.serial_number = v.serial_number;
            data.purchased_date = purchaseDate;

            const storeIDFill = v.purchase_form_store;
            const branchUUID = v.purchase_form_branch;
            if (isOther) {
                data.branch_name = v.branch_name;
            }
            data.branch_uuid = branchUUID;
            data.store_id = storeIDFill;

            const name = uuid.v4() + '.' + v.upload_file.name.split('.').pop();
            const fileData = {
                filename: name,
                category: 'customer_product_proof',
                type: v.upload_file.type,
            };

            const fileResponse = await getUploadUrl(fileData);

            if (fileResponse?.data?.success === true) {
                const fileUuid = fileResponse?.data?.data?.uuid;
                const s3UploadUrl = fileResponse?.data?.data?.url;
                const fullPath = fileResponse?.data?.data?.full_path;

                data.data = {
                    file: fileUuid,
                    file_path: fullPath,
                };

                const newFile = new File([v.upload_file], name, {
                    type: v.upload_file.type,
                });

                const uploadResponse = await fetch(s3UploadUrl, {
                    method: 'PUT',
                    body: newFile,
                });

                if (uploadResponse?.ok === true) {
                    const ret = await registerWarranty(data);

                    // Enhanced debug logging to help identify response structure issues
                    console.log('registerWarranty response:', {
                        status: ret.status,
                        data: ret.data,
                        success: ret.data?.success,
                        successType: typeof ret.data?.success,
                        fullResponse: ret,
                    });

                    // More robust success condition to handle different status codes and success value types
                    const isSuccessStatus = ret.status >= 200 && ret.status < 300;
                    const isSuccessResponse =
                        ret.data?.success === true ||
                        ret.data?.success === 'true' ||
                        (ret.data?.success !== false && ret.data?.data?.id);

                    if (isSuccessStatus && isSuccessResponse) {
                        toggle();
                        toast.success('Warranty Register Successfully');
                        nav('/member/view-warranty');
                    } else if (ret.status !== 401) {
                        // Don't show toast for 401 errors - handled by global interceptor
                        toggle();
                        let msg = '';

                        // Handle specific error cases with better logging
                        console.log('Error case triggered:', {
                            isSuccessStatus,
                            isSuccessResponse,
                            status: ret.status,
                            success: ret.data?.success,
                        });

                        if (isSuccessStatus && ret.data?.success === false) {
                            // API returned success status but with success: false
                            msg = ret.data?.error?.message || ret.data?.message || 'Registration failed';
                        } else if (ret.data?.status === 500) {
                            if (ret.data.error !== null) {
                                msg = ret.data.error.message;
                            } else {
                                msg = 'Register Fail';
                            }
                        } else if (!isSuccessStatus) {
                            msg = `HTTP Error ${ret.status}: ${ret.data?.error?.message || 'Registration failed'}`;
                        } else {
                            msg = ret.data?.error?.message || 'Registration failed';
                        }

                        toast.error(msg);
                        if (msg.includes('serial number')) {
                            setError('serial_number', ret.data?.error?.message || msg);
                        }
                    } else {
                        toggle();
                    }
                }
            }
        } catch (e) {
            toggle();
            toast.error('An error occurred. Please try again.');
        }
    };

    return (
        <>
            <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
                <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                    <div className="flex flex-wrap justify-between gap-3 p-4">
                        <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                            {t('label.register_your_product')}
                        </p>
                        <p className="text-[#5f678c] text-sm font-normal leading-normal m-0">
                            {t('label.register_product_desc')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Store Selection */}
                        <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                            <label className="flex flex-col flex-1">
                                <p className="text-gray-900 text-base font-medium pb-2 ">
                                    {t('form.purchase_form_store')}
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <div className="relative w-full">
                                    <select
                                        className={`w-full h-14 rounded-xl border ${errors.purchase_form_store ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base appearance-none cursor-pointer outline-none  focus:border-blue-500 focus-ring select-arrow transition-colors`}
                                        value={formData.purchase_form_store}
                                        onChange={(e) => onChgStore(e.target.value)}
                                    >
                                        <option value="">{t('form.please_select')}</option>
                                        {store.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.purchase_form_store && (
                                    <p className="text-red-500 text-sm mt-1">{errors.purchase_form_store}</p>
                                )}
                            </label>
                        </div>

                        {/* Purchase From */}
                        <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                            <label className="flex flex-col flex-1">
                                <p className="text-gray-900 text-base font-medium pb-2">
                                    {t('form.purchase_form_branch')}
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <div className="relative w-full">
                                    <select
                                        className={`w-full h-14 rounded-xl border ${errors.purchase_form_branch ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base appearance-none cursor-pointer outline-none focus:border-blue-500 focus-ring select-arrow transition-colors`}
                                        value={formData.purchase_form_branch}
                                        onChange={(e) => onChgBranch(e.target.value)}
                                    >
                                        <option value="">{t('form.please_select')}</option>
                                        {branch.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.purchase_form_branch && (
                                    <p className="text-red-500 text-sm mt-1">{errors.purchase_form_branch}</p>
                                )}
                            </label>
                        </div>

                        {/* Branch Name (if Other) */}
                        {isOther && (
                            <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                                <label className="flex flex-col flex-1">
                                    <p className="text-gray-900 text-base font-medium pb-2">
                                        {t('form.branch_name')}
                                        <span className="text-red-500 ml-1">*</span>
                                    </p>
                                    <input
                                        type="text"
                                        className={`w-full h-14 rounded-xl border ${errors.branch_name ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                        value={formData.branch_name}
                                        onChange={(e) => handleFieldChange('branch_name', e.target.value)}
                                        placeholder={t('form.branch_name')}
                                    />
                                    {errors.branch_name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.branch_name}</p>
                                    )}
                                </label>
                            </div>
                        )}

                        {/* Date of Purchase */}
                        <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                            <label className="flex flex-col flex-1">
                                <p className="text-gray-900 text-base font-medium pb-2">
                                    {t('form.date_of_purchase')}
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <div className="relative w-full date-input">
                                    <input
                                        type="date"
                                        className={`w-full h-14 rounded-xl border ${errors.date_of_purchase ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring cursor-pointer transition-colors`}
                                        value={formData.date_of_purchase}
                                        onChange={(e) => handleFieldChange('date_of_purchase', e.target.value)}
                                        max={dayjs().format('YYYY-MM-DD')}
                                    />
                                </div>
                                {errors.date_of_purchase && (
                                    <p className="text-red-500 text-sm mt-1">{errors.date_of_purchase}</p>
                                )}
                            </label>
                        </div>

                        {/* Serial Number */}
                        <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                            <label className="flex flex-col flex-1">
                                <p className="text-gray-900 text-base font-medium pb-2">
                                    {t('form.serial_number')}
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.serial_number ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                    value={formData.serial_number}
                                    onChange={(e) => {
                                        handleFieldChange('serial_number', e.target.value);
                                        setSerialNumber(e.target.value);
                                        // Clear product preview when serial number changes
                                        setProduct({});
                                    }}
                                    placeholder="Enter Serial Number"
                                />
                                {errors.serial_number && (
                                    <p className="text-red-500 text-sm mt-1">{errors.serial_number}</p>
                                )}
                            </label>
                        </div>

                        {/* File Upload */}
                        <div className="px-4 pb-3">
                            <div
                                className={`border-2 border-dashed ${dragOver ? 'border-blue-500 bg-blue-50' : errors.upload_file ? 'border-red-500' : 'border-gray-300'} rounded-xl p-6 text-center cursor-pointer transition-all duration-200 hover:border-gray-400`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragOver(true);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    setDragOver(false);
                                }}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('fileInput').click()}
                            >
                                <input
                                    id="fileInput"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <div className="mb-4">
                                    <p className="text-lg font-medium text-gray-900 mb-2">
                                        {formData.upload_file ? formData.upload_file.name : 'Proof of Purchase'}
                                    </p>
                                    <p className="text-sm text-gray-500 mb-4">
                                        {formData.upload_file
                                            ? `File selected: ${(formData.upload_file.size / (1024 * 1024)).toFixed(2)}MB`
                                            : 'Upload an image of your receipt or proof of purchase.'}
                                    </p>
                                </div>
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        className="flex cursor-pointer items-center justify-center overflow-hidden rounded-lg px-6 py-2 bg-gray-50 text-gray-900 text-sm font-bold leading-normal border border-gray-300 transition-colors duration-200 hover:bg-gray-100"
                                    >
                                        <span className="overflow-hidden">
                                            {formData.upload_file ? 'Change File' : 'Upload Image'}
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                                <p>Max File Size: 5MB</p>
                                <p>Only PNG, JPG, JPEG, PDF files are allowed.</p>
                            </div>
                            {errors.upload_file && (
                                <p className="text-red-500 text-sm mt-1 ml-0">{errors.upload_file}</p>
                            )}
                        </div>

                        {/* Product Preview - Only shown after successful validation */}
                        {product?.product_name && (
                            <div className="p-4">
                                <div className="flex gap-4 p-4 border border-green-300 rounded-lg bg-green-50">
                                    <img
                                        src={product.images_url}
                                        alt={product.product_name}
                                        className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <svg
                                                className="w-5 h-5 text-green-600"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            <span className="text-sm font-medium text-green-800">
                                                Serial Number Validated
                                            </span>
                                        </div>
                                        <p className="text-base font-medium text-gray-900 mb-1">
                                            Product: {product.product_name}
                                        </p>
                                        <p className="text-sm text-gray-600">Serial Number: {product.serial_number}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="p-4 flex justify-center">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl  h-12 px-4  bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                            >
                                <span className="overflow-hidden">{loading ? 'Loading...' : t('label.register')}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <SerialNumberExampleModal visible={egModal} onClose={() => setEgModal(false)} />
        </>
    );
};

export default RegisterProduct;
